import { useCallback } from 'react';
import { FieldName } from 'react-hook-form';

import { commonTranslationKeys, useTranslate } from '@dreryk/react-common';
import { Multiselect } from '@dreryk/react-components';

import { prescriptionTranslationKeys } from '../../../../../translations';
import { DraftPrescriptionsFormController } from '../../draftPrescriptions/DraftPrescriptionsForm.types';

import { AdditionalRequirement } from './additionalRequirements.types';

const ADDITIONAL_REQUIREMENTS: AdditionalRequirement[] = [
   {
      type: 'FOR_YEAR',
   },
   {
      type: 'SUBSTITUTE_FORBIDDEN',
   },
];

const useTriggerLabelExtractor = (): ((selectedAdditionalRequirements: AdditionalRequirement[]) => string) => {
   const translate = useTranslate();
   return useCallback(
      (selectedAdditionalRequirements: AdditionalRequirement[]): string =>
         selectedAdditionalRequirements
            ?.map((selectedValue) =>
               translate(prescriptionTranslationKeys.additionalRequirements.type[selectedValue.type])
            )
            .join(', ') ?? '',
      [translate]
   );
};

type BeanWithAdditionalRequirements<NAME extends string> = { [key in NAME]: AdditionalRequirement[] | null };

export type AdditionalRequirementsMultiSelectProps<NAME extends string> = Pick<
   DraftPrescriptionsFormController,
   'register'
> & {
   readonly name: FieldName<BeanWithAdditionalRequirements<NAME>>;
   readonly defaultValue?: AdditionalRequirement[] | null;
};

export const AdditionalRequirementsMultiSelect = <NAME extends string>(
   props: AdditionalRequirementsMultiSelectProps<NAME>
): JSX.Element => {
   return (
      <Multiselect<AdditionalRequirement>
         testId={'additionalRequirement'}
         controller={props.register(props.name)}
         defaultValue={props.defaultValue}
         itemKeyGenerator={'type'}
         triggerLabelExtractor={useTriggerLabelExtractor()}
         placeholder={commonTranslationKeys.choose}
         label={prescriptionTranslationKeys.additionalRequirements.others}
         maxHeight={190}
         compact
      >
         <>
            <Multiselect.SelectAll
               testId={'selectAll'}
               items={ADDITIONAL_REQUIREMENTS}
               selectAllLabel={commonTranslationKeys.choose}
            />
            <Multiselect.List items={ADDITIONAL_REQUIREMENTS} keyExtractor={'type'}>
               {(requirement) => (
                  <Multiselect.Item
                     item={requirement}
                     label={prescriptionTranslationKeys.additionalRequirements.type[requirement.type]}
                     testId={requirement.type}
                  />
               )}
            </Multiselect.List>
         </>
      </Multiselect>
   );
};
